import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { CurrencyInr, Calendar, CheckCircle, Warning, Plus, Trash, Receipt, ArrowCircleRight, PencilSimple, ArrowCounterClockwise, DownloadSimple } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { CollectPaymentDialog } from './finance/CollectPaymentDialog';
import { AdjustChargeDialog } from './finance/AdjustChargeDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtInr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const todayMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const todayIso = () => new Date().toISOString().split('T')[0];

const FINANCE_STATUS = {
  cleared: { label: 'CLEARED', bg: '#A3E635' },
  carried: { label: 'CARRIED', bg: '#FDE047' },
  due:     { label: 'DUE',     bg: '#F9A8D4' },
};

const getFinanceStatus = (row) => {
  if (row.is_cleared) return FINANCE_STATUS.cleared;
  if (row.carried_forward) return FINANCE_STATUS.carried;
  return FINANCE_STATUS.due;
};

const StatusBadge = ({ row }) => {
  const s = getFinanceStatus(row);
  return (
    <span className="px-2 py-1 text-black rounded text-xs font-bold border-2 border-black" style={{ backgroundColor: s.bg }}>
      {s.label}
    </span>
  );
};

const Finance = () => {
  const [month, setMonth] = useState(todayMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payOpen, setPayOpen] = useState(false);
  const [payRow, setPayRow] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payDate, setPayDate] = useState(todayIso());
  const [payNote, setPayNote] = useState('');

  // Adjust (charge override) dialog state
  const [adjOpen, setAdjOpen] = useState(false);
  const [adjRow, setAdjRow] = useState(null);
  const [adjAmount, setAdjAmount] = useState('');
  const [adjReason, setAdjReason] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}/finance/monthly`, { params: { month } });
      setData(res);
    } catch (e) {
      toast.error('Failed to load finance data');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openPay = (row) => {
    setPayRow(row);
    setPayAmount(String(row.balance > 0 ? row.balance : ''));
    setPayDate(todayIso());
    setPayNote('');
    setPayOpen(true);
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) { toast.error('Enter a positive amount'); return; }
    try {
      await axios.post(`${API}/candidates/${payRow.candidate_id}/payments`, {
        amount: amt, collected_date: payDate, note: payNote,
      });
      toast.success('Payment recorded');
      setPayOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record payment');
    }
  };

  const removePayment = async (candidateId, paymentId) => {
    if (!window.confirm('Remove this payment? Balance will be restored.')) return;
    try {
      await axios.delete(`${API}/candidates/${candidateId}/payments/${paymentId}`);
      toast.success('Payment removed');
      fetchData();
    } catch (err) {
      toast.error('Failed to remove payment');
    }
  };

  const openAdjust = (row) => {
    setAdjRow(row);
    setAdjAmount(row.has_override ? String(row.charge_override) : String(row.consultancy_charge));
    setAdjReason(row.override_reason || '');
    setAdjOpen(true);
  };

  const submitAdjust = async (e) => {
    e.preventDefault();
    const amt = parseFloat(adjAmount);
    if (isNaN(amt) || amt < 0) { toast.error('Enter a valid non-negative amount'); return; }
    try {
      await axios.patch(`${API}/candidates/${adjRow.candidate_id}/charge-override`, {
        amount: amt,
        reason: adjReason,
      });
      toast.success('Charge adjusted');
      setAdjOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to adjust charge');
    }
  };

  const clearAdjust = async () => {
    if (!window.confirm('Remove the adjustment? Charge will revert to the original consultancy charge.')) return;
    try {
      await axios.delete(`${API}/candidates/${adjRow.candidate_id}/charge-override`);
      toast.success('Adjustment removed');
      setAdjOpen(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to remove adjustment');
    }
  };

  return (
    <div data-testid="finance-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
            Finance
          </h1>
          <p className="text-base font-medium text-white">Consultancy charges, collections, and carry-forward dues</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            data-testid="finance-export-xlsx"
            onClick={async () => {
              try {
                const res = await axios.get(`${API}/finance/export.xlsx`, {
                  params: { month },
                  responseType: 'blob',
                });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = `TulsiJobs_Finance_${month}.xlsx`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                toast.success('Excel exported!');
              } catch {
                toast.error('Failed to export Excel');
              }
            }}
            className="bg-[#93C5FD] text-[#0A0A0A] border-2 border-[#0A0A0A] rounded-md font-bold px-4 py-2 shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-all"
          >
            <DownloadSimple size={20} weight="bold" className="mr-2" />
            Export Excel
          </Button>
          <Calendar size={24} weight="duotone" className="text-[#A3E635]" />
          <Input
            data-testid="finance-month-input"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="bg-white border-2 border-[#A3E635] rounded-md px-3 py-2 text-black font-bold"
          />
        </div>
      </div>

      {loading || !data ? (
        <div className="text-white font-bold">Loading…</div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
            <SummaryCard testid="finance-new-charges" bg="#93C5FD" icon={Receipt} label="New Charges" sub="This month" value={fmtInr(data.new_charges)} />
            <SummaryCard testid="finance-carried-forward" bg="#FDE047" icon={ArrowCircleRight} label="Carried Forward" sub="From prior months" value={fmtInr(data.carried_forward)} />
            <SummaryCard testid="finance-total-due" bg="#C4B5FD" icon={Warning} label="Total Due" sub="New + Carry" value={fmtInr(data.total_due)} />
            <SummaryCard testid="finance-collected" bg="#A3E635" icon={CheckCircle} label="Collected" sub="This month" value={fmtInr(data.collected_this_month)} />
            <SummaryCard testid="finance-outstanding" bg="#F9A8D4" icon={CurrencyInr} label="Outstanding" sub="Still to collect" value={fmtInr(data.all_outstanding)} />
          </div>

          {/* Per-candidate breakdown */}
          <div className="bg-[#0F3622] border-2 border-[#A3E635] rounded-md shadow-[4px_4px_0px_0px_rgba(163,230,53,0.3)] overflow-x-auto">
            <table className="w-full tracker-table" data-testid="finance-table">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Company</th>
                  <th>Joining</th>
                  <th>Charge</th>
                  <th>Paid (Total)</th>
                  <th>Paid This Month</th>
                  <th>Balance</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 ? (
                  <tr><td colSpan="9" className="text-center py-8 text-white">No candidates for this month.</td></tr>
                ) : (
                  data.rows.map((r) => {
                    const isFiredOrLeft = ['fired','left'].includes((r.joining_status || '').toLowerCase());
                    return (
                    <tr key={r.candidate_id} data-testid={`finance-row-${r.candidate_id}`}>
                      <td className="font-bold">
                        {r.candidate_name}
                        {isFiredOrLeft && (
                          <span className="ml-2 px-2 py-0.5 bg-[#F87171] text-white rounded text-[10px] font-bold uppercase border-2 border-black">
                            {r.joining_status}
                          </span>
                        )}
                      </td>
                      <td>{r.company_name}</td>
                      <td>{r.date_of_joining ? format(parseISO(r.date_of_joining), 'dd MMM yy') : '—'}</td>
                      <td className="font-bold">
                        {fmtInr(r.effective_charge)}
                        {r.has_override && (
                          <span
                            className="ml-1 px-1.5 py-0.5 bg-[#F59E0B] text-black rounded text-[10px] font-bold align-middle"
                            title={`Adjusted from ₹${r.consultancy_charge}${r.override_reason ? ' — ' + r.override_reason : ''}`}
                            data-testid={`adj-badge-${r.candidate_id}`}
                          >
                            ADJ
                          </span>
                        )}
                      </td>
                      <td>{fmtInr(r.total_paid)}</td>
                      <td className={r.paid_this_month > 0 ? 'font-bold text-[#A3E635]' : ''}>{fmtInr(r.paid_this_month)}</td>
                      <td className={r.balance > 0.01 ? 'font-bold text-[#FDE047]' : 'font-bold text-[#A3E635]'}>{fmtInr(r.balance)}</td>
                      <td>
                        <StatusBadge row={r} />
                      </td>
                      <td>
                        <div className="flex gap-1">
                          {!r.is_cleared && (
                            <Button
                              data-testid={`add-payment-${r.candidate_id}`}
                              onClick={() => openPay(r)}
                              className="bg-[#A3E635] text-black border-2 border-black rounded-md font-bold px-3 py-1 text-xs shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[3px_3px_0px_0px_rgba(10,10,10,1)] transition-all"
                            >
                              <Plus size={14} weight="bold" className="mr-1" />
                              Collect
                            </Button>
                          )}
                          <Button
                            data-testid={`adjust-charge-${r.candidate_id}`}
                            onClick={() => openAdjust(r)}
                            title="Adjust amount to collect (e.g. fired / left)"
                            className="bg-[#F59E0B] text-black border-2 border-black rounded-md font-bold px-3 py-1 text-xs shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[3px_3px_0px_0px_rgba(10,10,10,1)] transition-all"
                          >
                            <PencilSimple size={14} weight="bold" className="mr-1" />
                            Adjust
                          </Button>
                        </div>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Payment dialog */}
      <CollectPaymentDialog
        open={payOpen}
        onOpenChange={setPayOpen}
        row={payRow}
        amount={payAmount}
        setAmount={setPayAmount}
        date={payDate}
        setDate={setPayDate}
        note={payNote}
        setNote={setPayNote}
        onSubmit={submitPayment}
        onRemovePayment={removePayment}
      />

      {/* Adjust Charge Dialog */}
      <AdjustChargeDialog
        open={adjOpen}
        onOpenChange={setAdjOpen}
        row={adjRow}
        amount={adjAmount}
        setAmount={setAdjAmount}
        reason={adjReason}
        setReason={setAdjReason}
        onSubmit={submitAdjust}
        onClear={clearAdjust}
      />
    </div>
  );
};

const SummaryCard = ({ testid, bg, icon: Icon, label, sub, value }) => (
  <div
    data-testid={testid}
    className="border-2 border-black rounded-md p-4 shadow-[4px_4px_0px_0px_rgba(163,230,53,0.4)]"
    style={{ backgroundColor: bg }}
  >
    <div className="flex items-start justify-between mb-3">
      <Icon size={28} weight="duotone" />
    </div>
    <div className="text-2xl font-black text-black mb-1">{value}</div>
    <div className="text-xs font-bold uppercase tracking-wider text-black">{label}</div>
    <div className="text-xs text-black font-medium mt-1 opacity-70">{sub}</div>
  </div>
);

export default Finance;
